import React from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { maskCEP, maskCPF, maskDate } from '../../../utils/masks';
import { Circle } from '@phosphor-icons/react';

interface IClinica {
  id_clinicas_credenciadas: number;
  contrato: string;
  nome_comercial: string;
  razao_social: string;
  cpf_cnpj: string;
  dias_funcionamento: string;
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  nome_med: string;
  crm: string;
  crm_estado: string;
  trabalho_agendamento: string;
  pagamento_adiantado: string;
  telemedicina: string;
  observacao: string;
}

interface IAgendamento {
  cidade: string;
  cpf_cnpj_comer: string;
  cpf_colaborador: string;
  data_agendamento: string;
  estado: string;
  horario: string;
  id_agendamento: number;
  nome: string;
  nome_comercial: string;
  num_agenda: string;
  observacao: string;
  ordem_chegada: string;
  status: string;
  telemedicina: string;
  tipo_aso: string;
  usuario: string;
  linkDoatendimento: string;
}

interface IColaborador {
  id_colaboradores: number;
  matricula_empresa: string;
  matricula_esocial: string;
  nome_completo: string;
  cpf_colaborador: string;
  rg: string;
  data_nascimento: string;
  genero: string;
  cnh: string;
  email: string;
  telefone: string;
  situacao: string;
  nis: string;
  funcao: string;
  setor: string;
  turno: string;
  cnpj: string;
  razao_social: string;
  data_admissao: string;
  horario_trab_entrada: string;
  horario_trab_saida: string;
  local_obra: string;
  cod_ghe: string;
  ghe: string;
}

interface IEmpresaColab {
  bairro: string;
  cep: string;
  cidade: string;
  complemento: string;
  estado: string;
  numero: string;
  rua: string;
}

interface ITelefoneEmpresa {
  cnpj: string;
  id_empresa_telefone: number;
  telefone: string;
}

interface IExameGHE {
  id_exame_ghe: number;
  cod_ghe: string;
  exame_empresa: string;
  dias: string;
  data: [
    {
      data_agendamento: string;
    }
  ];
}

interface IGhe {
  cod_ghe: string;
  cod_obra: string;
  crm: string;
  crm_estado: string;
  exibir: string;
  id_cad_ghe: number;
  nome_ghe: string;
  nome_med: string;
  obs_aso: string;
  tipo_plano: string;
  valor_plano: string;
}

interface IRisco_Ghe {
  id_risco_ghe: number;
  cod_ghe: string;
  nome: string;
}

interface ITelefone_Credenciado {
  telefone: string;
}

interface IExamesToxico {
  cpf: string;
  exame_credenciado: string;
  exame_empresa: string;
  id_exame_colab: number;
  num_agenda: string;
  valor_credenciado: string;
  valor_empresa: string;
  data: [
    {
      data_agendamento: string;
    }
  ];
}

interface IExameHibrido {
  cpf_cnpj_comer: string;
  data_agendamento: string;
  exames: {
    exame_empresa: string;
  }[];
  horario: string;
  id_exame_hibrido: number;
  linkDoatendimento: string;
  nome_comercial: string;
  num_agenda: string;
  ordem_chegada: string;
  tipo: string;
  tipo_agendamento: string;
  dias_funcionamento:string
}

interface IPdf {
  clinica: IClinica[];
  agendamento: IAgendamento[];
  colaborador: IColaborador[];
  exames_ghe: IExameGHE[];
  exames_hibrido: IExameHibrido[]; // Adicione esta linha
  ghe: IGhe[];
  risco_ghe: IRisco_Ghe[];
  empresa_colab: IEmpresaColab[];
  telefone_empresa: ITelefoneEmpresa[];
  telefone_credenciado: ITelefone_Credenciado[];
  exames_toxi: IExamesToxico[];
}

export const GuiaHibrido: React.FC<IPdf[]> = (data, page) => {
  // console.log('estou em guia')
  // Primeiro, vamos processar cada exame_hibrido como um agendamento separado
  data[0].exames_hibrido.forEach((exameHibrido) => {
    // Encontra a clínica correspondente ao prestador de serviço
    const clinica = exameHibrido.nome_comercial // Fallback para a primeira clínica se não encontrar
    // console.log('clinica',clinica)
    // Cria um objeto de agendamento específico para este exame
    const agendamentoEspecifico = {
      ... data[0].agendamento[0],
      nome_comercial: clinica, // Usa o nome da clínica encontrada
      tipo_agendamento: exameHibrido.tipo_agendamento,
      linkDoatendimento: exameHibrido.linkDoatendimento,
    };
  
    // Prepara os dados para este PDF específico
    const dadosPdf = {
      ...data[0],
      
      clinica: [clinica], // Garante que estamos usando a clínica correta
      agendamento: [agendamentoEspecifico],
      exames_ghe: exameHibrido.exames.map((exame) => ({
        exame_empresa: exame.exame_empresa,
        data: [{ data_agendamento: data[0].agendamento[0].data_agendamento}],
      })),
      dias_funcionamento: data[0].exames_hibrido[0].dias_funcionamento
    };

    // Determina o tipo de página com base no tipo de agendamento
    const tipoPagina = exameHibrido.tipo_agendamento === 'telemedicina'? 'telemedicina' 
      : exameHibrido.tipo_agendamento === 'presencial' ? 'presencial' 
      : '';

    // Logica de Riscos (igual à original)
    const numberOfColumnsRiscos = 2;
    const numberOfRowsRiscos = Math.ceil(
      dadosPdf.risco_ghe.length / numberOfColumnsRiscos
    );

    const leftColumnRiscos = [];
    const rightColumnRiscos = [];
    const leftLengthRiscos = [];

    for (let i = 0; i < numberOfRowsRiscos; i++) {
      leftColumnRiscos[i] = dadosPdf.risco_ghe[i * numberOfColumnsRiscos];
      rightColumnRiscos[i] = dadosPdf.risco_ghe[i * numberOfColumnsRiscos + 1];
      leftLengthRiscos[i] = i;
    }

    const rightRiscos = rightColumnRiscos.map((right) => {
      if (right !== undefined) {
        return [{ text: right.nome ? right.nome : '', colSpan: 3 }, {}, {}];
      } else {
        return [{ text: '', colSpan: 3 }, {}, {}];
      }
    });
    
    const leftRiscos = leftColumnRiscos.map((left) => {
      if (left !== undefined) {
        return [{ text: left.nome ? left.nome : '', colSpan: 3 }, {}, {}];
      } else {
        return [{ text: '', colSpan: 3 }, {}, {}];
      }
    });

    const countRiscos = leftLengthRiscos.map((a, i) => [
      ...leftRiscos[i],
      ...rightRiscos[i]
    ]);

    // Logica de Exames (adaptada para exames_hibrido)
    const numberOfColumns = 2;
    const numberOfRows = Math.ceil(dadosPdf.exames_ghe.length / numberOfColumns);

    const leftColumn = [];
    const rightColumn = [];
    const leftLength = [];

    for (let i = 0; i < numberOfRows; i++) {
      leftColumn[i] = dadosPdf.exames_ghe[i * numberOfColumns];
      rightColumn[i] = dadosPdf.exames_ghe[i * numberOfColumns + 1];
      leftLength[i] = i;
    }

    const right = rightColumn.map((right, i) => {
      if (right !== undefined) {
        return [
          {
            text: right.exame_empresa ? right.exame_empresa : '',
            colSpan: 2,
            border: [true, true, false, true]
          },
          {},
          {
            text: `${
              right.data[0].data_agendamento
                ? maskDate(right.data[0].data_agendamento)
                : ''
            } `,
            border: [false, true, true, true],
            bold: true
          }
        ];
      } else {
        return [
          { text: '', colSpan: 2, border: [true, true, false, true] },
          {},
          { text: `${''}`, border: [false, true, true, true], bold: true }
        ];
      }
    });

    const left = leftColumn.map((left, i) => {
      if (left !== undefined) {
        return [
          {
            text: left.exame_empresa ? left.exame_empresa : '',
            colSpan: 2,
            border: [true, true, false, true]
          },
          {},
          {
            text: `${
              left.data[0].data_agendamento
                ? maskDate(left.data[0].data_agendamento)
                : ''
            } `,
            border: [false, true, true, true],
            bold: true
          }
        ];
      } else {
        return [
          { text: '', colSpan: 2, border: [true, true, false, true] },
          {},
          { text: `${''}`, border: [false, true, true, true], bold: true }
        ];
      }
    });

    const countExames = leftLength.map((a, i) => [...left[i], ...right[i]]);

    // Função para remover dados da clínica (adaptada)
    const removeDataClinic = () => {
      if (tipoPagina === 'telemedicina') {
        return [
          [
            {
              text: 'Clínica',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: clinica, colSpan: 5 }, // Usa clinicaParaPdf aqui
            {},
            {},
            {},
            {}
          ]
        ];
      } else {
        return [
          [
            {
              text: 'Clínica',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: clinica, colSpan: 2 }, // Usa clinicaParaPdf aqui
            {},
            {
              text: 'Atendimento',
              fillColor: '#88CBC5',
              color: '#000',
              bold: true,
              alignment: 'center'
            },
            { text: data[0].exames_hibrido[0].dias_funcionamento, colSpan: 2 }, // Usa clinicaParaPdf aqui
            {}
          ],
        ];
      }
    };

    // Função linkDoAtendimento (adaptada)
    const linkDoAtendimento = () => {
      if (tipoPagina === 'telemedicina') {
        return [
          {
            text: 'Resp. do PCMSO',
            fillColor: '#88CBC5',
            color: '#000',
            bold: true,
            alignment: 'center',
            fontSize: 9
          },
          { text: dadosPdf.ghe[0]?.nome_med },
          {
            text: 'CRM',
            fillColor: '#88CBC5',
            color: '#000',
            bold: true,
            alignment: 'center'
          },
          { text: `${dadosPdf.ghe[0]?.crm}/${dadosPdf.ghe[0]?.crm_estado}` },
          {
            text: 'Link do atendimento',
            fillColor: '#88CBC5',
            color: '#000',
            bold: true,
            alignment: 'center'
          },
          { text: agendamentoEspecifico.linkDoatendimento }
        ];
      } else {
        return [
          {
            text: 'Resp. do PCMSO',
            fillColor: '#88CBC5',
            color: '#000',
            bold: true,
            alignment: 'center',
            fontSize: 9
          },
          { text: dadosPdf.ghe[0]?.nome_med, colSpan: 3 },
          {},
          {},
          {
            text: 'CRM',
            fillColor: '#88CBC5',
            color: '#000',
            bold: true,
            alignment: 'center'
          },
          { text: `${dadosPdf.ghe[0]?.crm}/${dadosPdf.ghe[0]?.crm_estado}` }
        ];
      }
    };

    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    const reportTitle = [
      {
        text: '',
        fontSize: 15,
        bold: true,
        margin: [30, 30, 0, 45]
      }
    ];

    const details = [
      {
        style: { fontSize: 10 },
        color: '#444',
        table: {
          widths: [80, 120, 60, 90, '*', 80],
          headerRows: 2,
          body: [
            [
              {
                text: [
                  {
                    text:
                      tipoPagina === ''
                        ? String('Guia de encaminhamento ').toLocaleUpperCase()
                        : tipoPagina === 'telemedicina'
                        ? String('Guia de encaminhamento telemedicina').toLocaleUpperCase()
                        : tipoPagina === 'presencial'
                        ? String('Guia de encaminhamento presencial').toLocaleUpperCase()
                        : String('Guia de encaminhamento').toLocaleUpperCase(),
                    style: 'tableHeader',
                    fontSize: 18,
                    alignment: 'center'
                  },
                  {
                    text: '\n(11) 96621 8866 – www.aryz.com.br - agendamento@aryz.com.br ',
                    alignment: 'center'
                  }
                ],
                colSpan: 6,
                color: '#6D539E'
              },
              {},
              {},
              {},
              {},
              {}
            ],
            [
              {
                text: 'Dados do Prestador de Serviço',
                colSpan: 6,
                style: 'tableHeader',
                alignment: 'center',
                fillColor: '#6D539E',
                color: '#FFF',
                bold: true
              },
              {},
              {},
              {},
              {},
              {}
            ],
            ...removeDataClinic(),

            [
              {
                text: 'Dados da Empresa',
                colSpan: 6,
                style: 'tableHeader',
                alignment: 'center',
                fillColor: '#6D539E',
                color: '#FFF',
                bold: true
              },
              {},
              {},
              {},
              {},
              {}
            ],
            [
              {
                text: 'Empresa',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: dadosPdf.colaborador[0].razao_social, colSpan: 2 },
              {},
              {
                text: 'CNPJ',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: dadosPdf.colaborador[0].cnpj, colSpan: 2 },
              {}
            ],

            [
              {
                text: 'Dados do Colaborador',
                colSpan: 6,
                style: 'tableHeader',
                alignment: 'center',
                fillColor: '#6D539E',
                color: '#FFF',
                bold: true
              }
            ],
            [
              {
                text: 'Colaborador',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: dadosPdf.colaborador[0].nome_completo, colSpan: 5 }
            ],
            [
              {
                text: 'Setor',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: dadosPdf.colaborador[0].setor },
              {
                text: 'Função',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: dadosPdf.colaborador[0].funcao, colSpan: 3 }
            ],
            [
              {
                text: 'CPF',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              {
                text: dadosPdf.colaborador[0].cpf_colaborador
                  ? maskCPF(dadosPdf.colaborador[0].cpf_colaborador)
                  : dadosPdf.colaborador[0].cpf_colaborador
              },
              {
                text: 'RG ou CTPS',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: dadosPdf.colaborador[0].rg, colSpan: 3 }
            ],
            [
              {
                text: 'Nascimento',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: maskDate(dadosPdf.colaborador[0].data_nascimento) },
              {
                text: 'GHE',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: dadosPdf.colaborador[0].ghe },
              {
                text: 'Sexo',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: dadosPdf.colaborador[0].genero }
            ],

            [
              {
                text: 'Dados do Agendamento',
                colSpan: 6,
                style: 'tableHeader',
                alignment: 'center',
                fillColor: '#6D539E',
                color: '#FFF',
                bold: true
              }
            ],
            linkDoAtendimento(),
            [
              {
                text: 'Tipo de ASO',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: agendamentoEspecifico.tipo_aso },
              {
                text: 'Data',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              {
                text: agendamentoEspecifico?.data_agendamento
                  ? maskDate(agendamentoEspecifico?.data_agendamento)
                  : agendamentoEspecifico?.data_agendamento
              },
              {
                text: 'Horário',
                fillColor: '#88CBC5',
                color: '#000',
                bold: true,
                alignment: 'center'
              },
              { text: agendamentoEspecifico?.horario }
            ],

            [
              {
                text: 'Riscos Ocupacionais',
                colSpan: 6,
                style: 'tableHeader',
                alignment: 'center',
                fillColor: '#6D539E',
                color: '#FFF',
                bold: true
              }
            ],

            ...countRiscos,

            [
              {
                text: 'Exames Complementares a Serem Realizados',
                colSpan: 6,
                style: 'tableHeader',
                alignment: 'center',
                fillColor: '#6D539E',
                color: '#FFF',
                bold: true
              }
            ],

            ...countExames,

            [
              {
                text: 'Observações',
                colSpan: 6,
                style: 'tableHeader',
                alignment: 'center',
                fillColor: '#6D539E',
                color: '#FFF',
                bold: true
              }
            ],
            [
              { text: agendamentoEspecifico?.observacao, colSpan: 6, rowSpan: 3 }
            ],
            [{}, {}, {}, {}, {}, {}],
            [{}, {}, {}, {}, {}, {}]
          ]
        }
      }
    ];

    function Rodape(currentPage: string, pageCounf: string) {
      return [
        {
          text: `${currentPage} / ${pageCounf}`,
          alignment: 'right',
          fontSize: 9,
          margin: [0, 10, 20, 0]
        }
      ];
    }

    const docDefinitions: any = {
      info: {
        title: dadosPdf.colaborador[0].nome_completo,
        subject: dadosPdf.colaborador[0].nome_completo,
        creator: dadosPdf.colaborador[0].nome_completo,
        producer: dadosPdf.colaborador[0].nome_completo,
        keywords: dadosPdf.colaborador[0].nome_completo,
        download: dadosPdf.colaborador[0].nome_completo,
        filename: dadosPdf.colaborador[0].nome_completo,
        downloadName: dadosPdf.colaborador[0].nome_completo
      },
      pageSize: 'A4',
      pageMargins: [38, 50, 50, 40],
      header: [reportTitle],
      content: [details],
      fontSize: 5,
      footer: Rodape
    };

    // Gera um PDF para cada exame_hibrido
    pdfMake
      .createPdf(docDefinitions)
      .download(
        `GUIA ARYZ - ${dadosPdf.colaborador[0].nome_completo.replace('.', ' ')} - ${exameHibrido.nome_comercial}`
      );
  });

  return <div />;
};
